import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { getEmployeeProfile } from '../../../shared/api/employee.service';
import { useEmployeeSession } from '../../../shared/hooks/use-employee-session';
import { rubikTypographyStyles } from '../../../shared/ui';
import { useRegistrationCompletePreviewContent } from '../model';

const HIGHLIGHTED_DONATION_INDEX = 2;
type RegistrationCompleteDonationCardVariant = 'compact' | 'preview';

type DonationCardMetrics = {
  amountFontSizePx: number;
  buttonFontSizePx: number;
  buttonHorizontalPaddingPx: number;
  buttonRadiusPx: number;
  buttonVerticalPaddingPx: number;
  cardMinHeightPx: number;
  cardRadiusPx: number;
  cardPaddingXpx: number;
  cardPaddingYpx: number;
  gapPx: number;
  optionFontSizePx: number;
  optionMinHeightPx: number;
  optionRadiusPx: number;
  rowGapPx: number;
};

const donationCardMetricsByVariant: Record<
  RegistrationCompleteDonationCardVariant,
  DonationCardMetrics
> = {
  compact: {
    amountFontSizePx: 22,
    buttonFontSizePx: 8,
    buttonHorizontalPaddingPx: 8,
    buttonRadiusPx: 5,
    buttonVerticalPaddingPx: 3,
    cardMinHeightPx: 105,
    cardRadiusPx: 8,
    cardPaddingXpx: 13,
    cardPaddingYpx: 11,
    gapPx: 8,
    optionFontSizePx: 10,
    optionMinHeightPx: 24,
    optionRadiusPx: 5,
    rowGapPx: 4,
  },
  preview: {
    amountFontSizePx: 29,
    buttonFontSizePx: 11,
    buttonHorizontalPaddingPx: 10,
    buttonRadiusPx: 6,
    buttonVerticalPaddingPx: 4,
    cardMinHeightPx: 138,
    cardRadiusPx: 10,
    cardPaddingXpx: 17,
    cardPaddingYpx: 14,
    gapPx: 10,
    optionFontSizePx: 13,
    optionMinHeightPx: 31,
    optionRadiusPx: 6,
    rowGapPx: 6,
  },
};

const DonationCard = styled.article.attrs({
  'data-ui': 'registration-complete-donation-card',
})<{ $metrics: DonationCardMetrics }>`
  min-height: ${({ $metrics, theme }) =>
    theme.scale.px($metrics.cardMinHeightPx)};
  width: 100%;
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.surface.base};
  border-radius: ${({ $metrics, theme }) =>
    theme.scale.px($metrics.cardRadiusPx)};
  padding: ${({ $metrics, theme }) => theme.scale.px($metrics.cardPaddingYpx)}
    ${({ $metrics, theme }) => theme.scale.px($metrics.cardPaddingXpx)};
  display: grid;
  justify-items: center;
  align-content: start;
  gap: ${({ $metrics, theme }) => theme.scale.px($metrics.gapPx)};
`;

const Amount = styled.p<{ $metrics: DonationCardMetrics }>`
  ${rubikTypographyStyles};
  margin: 0;
  font-size: ${({ $metrics, theme }) =>
    theme.scale.px($metrics.amountFontSizePx)};
  font-weight: 600;
  line-height: 1.18;
  color: ${({ theme }) => theme.colors.text.primary};

  & > span {
    color: ${({ theme }) => theme.colors.border.emphasis};
  }
`;

const InsertValueTag = styled.span<{ $metrics: DonationCardMetrics }>`
  ${rubikTypographyStyles};
  background: ${({ theme }) => theme.colors.brand.primary};
  border-radius: ${({ $metrics, theme }) =>
    theme.scale.px($metrics.buttonRadiusPx)};
  padding: ${({ $metrics, theme }) =>
      theme.scale.px($metrics.buttonVerticalPaddingPx)}
    ${({ $metrics, theme }) =>
      theme.scale.px($metrics.buttonHorizontalPaddingPx)};
  font-size: ${({ $metrics, theme }) =>
    theme.scale.px($metrics.buttonFontSizePx)};
  font-weight: 600;
  line-height: 1.18;
`;

const DonationRow = styled.div<{ $metrics: DonationCardMetrics }>`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: ${({ $metrics, theme }) => theme.scale.px($metrics.rowGapPx)};
`;

const DonationOption = styled.span<{
  $isSelected: boolean;
  $metrics: DonationCardMetrics;
}>`
  ${rubikTypographyStyles};
  min-height: ${({ $metrics, theme }) =>
    theme.scale.px($metrics.optionMinHeightPx)};
  border-radius: ${({ $metrics, theme }) =>
    theme.scale.px($metrics.optionRadiusPx)};
  background: ${({ $isSelected }) =>
    $isSelected ? 'rgba(255, 190, 0, 0.5)' : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ $metrics, theme }) =>
    theme.scale.px($metrics.optionFontSizePx)};
  font-weight: ${({ $isSelected }) => ($isSelected ? 600 : 400)};
  line-height: 1.2;
`;

type RegistrationCompleteDonationCardProps = {
  variant?: RegistrationCompleteDonationCardVariant;
};

export function RegistrationCompleteDonationCard({
  variant = 'compact',
}: RegistrationCompleteDonationCardProps) {
  const { t } = useTranslation();
  const { authContact, status } = useEmployeeSession();
  const metrics = donationCardMetricsByVariant[variant];
  const profileQuery = useQuery({
    enabled: status === 'authenticated',
    queryFn: getEmployeeProfile,
    queryKey: ['employee', 'profile'],
  });
  const { donationCurrencySymbol, donationOptionLabels, donationValueLabel } =
    useRegistrationCompletePreviewContent({
      authContact,
      profile: profileQuery.data ?? null,
    });

  return (
    <DonationCard $metrics={metrics}>
      <Amount $metrics={metrics}>
        <span>{donationCurrencySymbol}</span> {donationValueLabel}
      </Amount>

      <InsertValueTag $metrics={metrics}>
        {t('registrationComplete.insertValueLabel')}
      </InsertValueTag>

      <DonationRow $metrics={metrics}>
        {donationOptionLabels.map((amount, index) => (
          <DonationOption
            key={amount}
            $isSelected={index === HIGHLIGHTED_DONATION_INDEX}
            $metrics={metrics}
          >
            {amount}
          </DonationOption>
        ))}
      </DonationRow>
    </DonationCard>
  );
}
